import React, { useEffect, useState } from "react";
import { Modal, Button, TextInput, Checkbox } from "@mantine/core";
import { supabase } from "../../config/supabaseClient";
import styled from "styled-components";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import { Select } from "@mantine/core";

const Container = styled.div`
  max-width: 400px;
  margin: auto;
  padding: 0 20px;

  @media (min-width: 1200px) {
    padding: 0 40px;
  }
`;
const EditorContainer = styled.div`
  margin-bottom: 1rem;
  border: 1px solid #ccc;
  border-radius: 5px;
  overflow: hidden;

  .ProseMirror {
    padding: 0.75rem;
    min-height: 150px;
    outline: none;

    p {
      margin: 0.5em 0;
    }

    h1,
    h2,
    h3,
    h4,
    h5,
    h6 {
      margin: 1em 0 0.5em;
    }

    ul,
    ol {
      padding-left: 1.5em;
    }

    &:focus {
      border-color: rgb(167, 140, 76)!important;
    }
  }
`;
const MenuBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  padding: 0.5rem;
  border-bottom: 1px solid #ccc;
  background-color: #f5f5f5;

  button {
    margin-right: 0.25rem;
    margin-bottom: 0.25rem;
    padding: 0.25rem 0.5rem;
    border: 1px solid #ccc;
    background-color: white;
    border-radius: 3px;
    cursor: pointer;

    &:hover {
      background-color: #f0f0f0;
    }

    &.is-active {
      background-color: rgb(167, 140, 76)!important;
      color: white;
    }
  }
`;
const LinkInput = styled.div`
  display: ${(props) => (props.isVisible ? "flex" : "none")};
  align-items: center;
  margin: 0.5rem;

  input {
    flex: 1;
    padding: 0.25rem 0.5rem;
    border: 1px solid #ccc;
    border-radius: 3px;
    margin-right: 0.5rem;
  }

  button {
    padding: 0.25rem 0.5rem;
    background-color: rgb(167, 140, 76)!important;
    color: white;
    border: none;
    border-radius: 3px;
    cursor: pointer;

    &:hover {
      background-color: #bc9f8b;
    }
  }
`;
const InputField = styled.input`
  width: 100%;
  padding: 0.75rem;
  margin-bottom: 1rem;
  border: 1px solid #ccc;
  border-radius: 5px;
  font-size: 16px;

  &:focus {
    border-color: rgb(167, 140, 76)!important;
    outline: none;
  }
`;
const Tiptap = ({ onChange, content }) => {
  const [linkUrl, setLinkUrl] = useState("");
  const [showLinkInput, setShowLinkInput] = useState(false);

  const editor = useEditor({
    extensions: [
      StarterKit,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: "article-link",
        },
      }),
    ],
    content: content || "",
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
  });

  const setLink = () => {
    if (!editor) return;

    // Check if text is selected
    if (editor.state.selection.empty) {
      alert("Please select some text first");
      return;
    }

    // If URL is provided
    if (linkUrl) {
      // Add https:// if protocol is missing
      const url = linkUrl.trim().startsWith("http")
        ? linkUrl.trim()
        : `https://${linkUrl.trim()}`;

      editor
        .chain()
        .focus()
        .extendMarkRange("link")
        .setLink({ href: url })
        .run();

      setLinkUrl("");
      setShowLinkInput(false);
    } else {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
    }
  };

  if (!editor) {
    return null;
  }

  return (
    <EditorContainer>
      <MenuBar>
        <button
          onClick={() => editor.chain().focus().toggleBold().run()}
          className={editor.isActive("bold") ? "is-active" : ""}
        >
          Bold
        </button>
        <button
          onClick={() => editor.chain().focus().toggleItalic().run()}
          className={editor.isActive("italic") ? "is-active" : ""}
        >
          Italic
        </button>
        <button
          onClick={() => {
            // Only apply heading if text is selected, otherwise do nothing
            if (!editor.state.selection.empty) {
              editor.chain().focus().toggleHeading({ level: 1 }).run();
            } else {
              alert("Please select some text first");
            }
          }}
          className={
            editor.isActive("heading", { level: 1 }) ? "is-active" : ""
          }
        >
          H1
        </button>
        <button
          onClick={() => {
            // Only apply heading if text is selected, otherwise do nothing
            if (!editor.state.selection.empty) {
              editor.chain().focus().toggleHeading({ level: 2 }).run();
            } else {
              alert("Please select some text first");
            }
          }}
          className={
            editor.isActive("heading", { level: 2 }) ? "is-active" : ""
          }
        >
          H2
        </button>
        <button
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          className={editor.isActive("bulletList") ? "is-active" : ""}
        >
          Bullet List
        </button>
        <button
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          className={editor.isActive("orderedList") ? "is-active" : ""}
        >
          Ordered List
        </button>
        <button
          onClick={() => setShowLinkInput(!showLinkInput)}
          className={editor.isActive("link") ? "is-active" : ""}
        >
          Link
        </button>
        {editor.isActive("link") && (
          <button onClick={() => editor.chain().focus().unsetLink().run()}>
            Unlink
          </button>
        )}
      </MenuBar>
      <LinkInput isVisible={showLinkInput}>
        <input
          type="text"
          placeholder="Enter URL"
          value={linkUrl}
          onChange={(e) => setLinkUrl(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              setLink();
            }
          }}
        />
        <button onClick={setLink}>Add Link</button>
      </LinkInput>
      <EditorContent editor={editor} />
    </EditorContainer>
  );
};

const UploadProject = () => {
  const [projects, setProjects] = useState([]);
  const [activeProjects, setActiveProjects] = useState([]);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [viewProject, setViewProject] = useState(null);
  const [newProject, setNewProject] = useState({
    title: "",
    content: "",
    area: "",
    price: "",
    location: "",
    images: "",
    status: true,
    sold: false,
  });
  const [currentProject, setCurrentProject] = useState(null);

  const handleContentChange = (html) => {
    setNewProject({
      ...newProject,
      content: html,
    });
  };

  const fetchAllProjects = async () => {
    const { data, error } = await supabase
      .from("projects")
      .select("*")
      .eq("softDelete", false);
    if (!error) setProjects(data);
  };

  const fetchActiveProjects = async () => {
    const { data, error } = await supabase
      .from("projects")
      .select("*")
      .eq("status", true)
      .eq("softDelete", false);
    if (!error) setActiveProjects(data);
  };

  useEffect(() => {
    fetchAllProjects();
    fetchActiveProjects();
  }, []);

  const handleAddProject = async () => {
    try {
      // Step 1: Upload all images to Supabase Storage
      const uploadedImageUrls = [];

      for (const file of newProject.images) {
        const fileExt = file.name.split(".").pop();
        const uniqueName = `${Date.now()}-${Math.random()
          .toString(36)
          .substring(2, 9)}.${fileExt}`;

        const { data: imageData, error: imageError } = await supabase.storage
          .from("project-images") // replace with your bucket name
          .upload(uniqueName, file);

        if (imageError) {
          console.error("Error uploading image:", imageError);
          return;
        }

        const { data: publicUrlData } = supabase.storage
          .from("project-images")
          .getPublicUrl(imageData.path);

        uploadedImageUrls.push(publicUrlData.publicUrl);
      }

      // Step 2: Insert the project record into the database
      const { error } = await supabase.from("projects").insert([
        {
          ...newProject,
          images: uploadedImageUrls,
          price: parseInt(newProject.price),
          softDelete: false,
        },
      ]);

      if (error) {
        console.error("Error inserting project:", error);
      } else {
        console.log("Project added successfully");
        setAddModalOpen(false);
        setNewProject({
          title: "",
          content: "",
          area: "",
          price: "",
          location: "",
          images: [],
          status: true,
          sold: false,
        });
      }
    } catch (err) {
      console.error("Unexpected error:", err);
    }
  };

  const handleEditProject = async () => {
    const imagesArray = currentProject.images
      .split(",")
      .map((img) => img.trim());
    const { error } = await supabase
      .from("projects")
      .update({
        ...currentProject,
        images: imagesArray,
        price: parseInt(currentProject.price),
      })
      .eq("id", currentProject.id);
    if (!error) {
      fetchAllProjects();
      fetchActiveProjects();
      setEditModalOpen(false);
      setCurrentProject(null);
    }
  };

  const handleSoftDeleteProject = async (projectId) => {
  const { error } = await supabase
    .from("projects")
    .delete()
    .eq("id", projectId);

  if (!error) {
    fetchAllProjects();
    fetchActiveProjects();
  } else {
    console.error("Delete failed:", error);
  }
};


  const handleChange = (e) => {
    const files = Array.from(e.target.files);
    setNewProject((prev) => ({
      ...prev,
      images: [...prev.images, ...files], // store File objects
    }));
  };

  return (
    <div>
      <Button
        style={{ marginTop: "8rem", marginBottom: "20px" }}
        onClick={() => setAddModalOpen(true)}
      >
        Add New Project
      </Button>

      <Modal
        opened={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        title="Add New Project"
        size={"xl"}
        centered
      >
        <TextInput
          label="TITLE"
          value={newProject.title}
          onChange={(e) =>
            setNewProject({ ...newProject, title: e.target.value })
          }
          mt="sm"
        />

        <label>Article Content</label>
        {/* Assuming you have TipTap integrated */}
        <Tiptap onChange={handleContentChange} content={newProject.content} />

        <TextInput
          label="AREA"
          value={newProject.area}
          onChange={(e) =>
            setNewProject({ ...newProject, area: e.target.value })
          }
          mt="sm"
        />

        <TextInput
          label="PRICE"
          value={newProject.price}
          onChange={(e) =>
            setNewProject({ ...newProject, price: e.target.value })
          }
          mt="sm"
        />

        <TextInput
          label="LOCATION"
          value={newProject.location}
          onChange={(e) =>
            setNewProject({ ...newProject, location: e.target.value })
          }
          mt="sm"
        />

        <label>Images</label>
        <InputField
          type="file"
          name="images"
          multiple
          onChange={handleChange}
          required
        />

        {/* Display uploaded images */}
        {newProject.images.length > 0 && (
          <div style={{ marginTop: "1rem" }}>
            <h4>Uploaded Images</h4>
            <div style={{ display: "flex", flexWrap: "wrap" }}>
              {console.log("dfe", newProject)}
              {newProject.images.map((img, idx) => (
                <img
                  key={idx}
                  src={URL.createObjectURL(img)}
                  alt={`Uploaded Image ${idx}`}
                  style={{ width: "100px", height: "100px", margin: "10px" }}
                />
              ))}
            </div>
          </div>
        )}

        <Select
          label="Status"
          data={[
            { value: "true", label: "Active" },
            { value: "false", label: "Inactive" },
          ]}
          value={String(newProject.status)}
          onChange={(value) =>
            setNewProject({ ...newProject, status: value === "true" })
          }
          mt="sm"
        />

        <Select
          label="Sold"
          data={[
            { value: "true", label: "Sold" },
            { value: "false", label: "Unsold" },
          ]}
          value={String(newProject.sold)}
          onChange={(value) =>
            setNewProject({ ...newProject, sold: value === "true" })
          }
          mt="sm"
        />

        <Button fullWidth mt="md" onClick={handleAddProject}>
          Submit
        </Button>
      </Modal>

      {/* EDIT MODAL */}
      <Modal
        opened={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        title="Edit Project"
        centered
        size="xl"
      >
        {currentProject && (
          <>
            <TextInput
              label="TITLE"
              value={currentProject.title}
              onChange={(e) =>
                setCurrentProject({ ...currentProject, title: e.target.value })
              }
              mt="sm"
            />

            <label>Article Content</label>
            <Tiptap
              content={currentProject.content}
              onChange={(value) =>
                setCurrentProject({ ...currentProject, content: value })
              }
            />

            <TextInput
              label="AREA"
              value={currentProject.area}
              onChange={(e) =>
                setCurrentProject({ ...currentProject, area: e.target.value })
              }
              mt="sm"
            />

            <TextInput
              label="PRICE"
              value={currentProject.price}
              onChange={(e) =>
                setCurrentProject({ ...currentProject, price: e.target.value })
              }
              mt="sm"
            />

            <TextInput
              label="LOCATION"
              value={currentProject.location}
              onChange={(e) =>
                setCurrentProject({
                  ...currentProject,
                  location: e.target.value,
                })
              }
              mt="sm"
            />
            {/* <Select
              label="Status"
              data={[
                { value: "true", label: "Active" },
                { value: "false", label: "Inactive" },
              ]}
              value={String(newProject.status)}
              onChange={(value) =>
                setNewProject({ ...newProject, status: value === "true" })
              }
              mt="sm"
            /> */}

            {/* <Select
              label="Sold"
              data={[
                { value: "true", label: "Sold" },
                { value: "false", label: "Unsold" },
              ]}
              value={String(newProject.sold)}
              onChange={(value) =>
                setNewProject({ ...newProject, sold: value === "true" })
              }
              mt="sm"
            /> */}
  <Select
  label="Status"
  data={[
    { value: "true", label: "Active" },
    { value: "false", label: "Inactive" },
  ]}
  value={String(currentProject.status)}
  onChange={(value) =>
    setCurrentProject({ ...currentProject, status: value === "true" })
  }
  mt="sm"
/>

<Select
  label="Sold"
  data={[
    { value: "true", label: "Sold" },
    { value: "false", label: "Unsold" },
  ]}
  value={String(currentProject.sold)}
  onChange={(value) =>
    setCurrentProject({ ...currentProject, sold: value === "true" })
  }
  mt="sm"
/>

            <Button fullWidth mt="md" onClick={handleEditProject}>
              Update
            </Button>
          </>
        )}
      </Modal>

      {/* VIEW MODAL */}
      <Modal
        opened={viewModalOpen}
        onClose={() => setViewModalOpen(false)}
        title="Project Details"
        centered
        size="lg"
      >
        {viewProject && (
          <div>
            <h2>{viewProject.title}</h2>
            <p>{viewProject.content}</p>
            <p>Area: {viewProject.area}</p>
            <p>Price: AED {viewProject.price}</p>
            <p>Location: {viewProject.location}</p>
            <p>Status: {viewProject.status ? "Available" : "Not Available"}</p>
            <p>Sold: {viewProject.sold ? "Yes" : "No"}</p>
            <div style={{ display: "flex", flexWrap: "wrap" }}>
              {viewProject.images?.map((img, idx) => (
                <img
                  key={idx}
                  src={img}
                  alt={`project-${idx}`}
                  style={{ width: "150px", margin: "10px" }}
                />
              ))}
            </div>
          </div>
        )}
      </Modal>

      {/* PROJECTS LIST */}
      <h1>All Projects</h1>
      {projects.length === 0 ? (
        <p>No projects available.</p>
      ) : (
        projects.map((project) => (
          <div
            key={project.id}
            style={{
              border: "1px solid #ccc",
              margin: "10px",
              padding: "10px",
            }}
          >
            <h2>{project.title}</h2>
            <p>{project.content}</p>
            <Button
              onClick={() => {
                setViewProject(project);
                setViewModalOpen(true);
              }}
              style={{ marginRight: "10px" }}
            >
              View
            </Button>
            <Button
              onClick={() => {
                setCurrentProject({
                  ...project,
                  images: project.images.join(", "),
                });
                setEditModalOpen(true);
              }}
              style={{ marginRight: "10px" }}
            >
              Edit
            </Button>
            <Button
              color="red"
              onClick={() => handleSoftDeleteProject(project.id)}
            >
              Delete
            </Button>
          </div>
        ))
      )}
    </div>
  );
};

export default UploadProject;
